import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, message, Modal } from 'antd';
import { SendOutlined, ClearOutlined, LoginOutlined, AudioOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useChatStore, Message } from '../../../store/chatStore';
import { useAuthStore } from '../../../store/authStore';
import { sendChatMessageStream } from '../../../api/chat';
import { transcribeAudio, speechService } from '../../../api/speech';
import './InputArea.css';

const SCROLL_THRESHOLD = 200; // 滚动阈值，超过这个高度时固定输入框

const { TextArea } = Input;
const MAX_LENGTH = 500;
/** 未登录用户在同一对话窗口内最多可发送的用户消息条数（多轮对话） */
const MAX_GUEST_ROUNDS_PER_CONVERSATION = 3;

interface InputAreaProps {
  onSend?: () => void;
  /** 点击「需要登录」弹窗中的去登录时调用，用于打开登录模态（不传则跳转登录页） */
  onRequestLogin?: () => void;
}

const InputArea: React.FC<InputAreaProps> = ({ onSend, onRequestLogin }) => {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [inputType, setInputType] = useState<'text' | 'voice'>('text'); // 新增：输入方式切换状态
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isCancelArea, setIsCancelArea] = useState(false);
  const isCancelAreaRef = useRef(false);
  const [showVoiceOverlay, setShowVoiceOverlay] = useState(false);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [loginReason, setLoginReason] = useState<'limit' | 'voice'>('limit');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const textAreaRef = useRef<any>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 清理录音资源
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const {
    conversations,
    currentConversationId,
    createConversation,
    promoteTemporaryConversation,
    addMessage,
    updateMessage,
    deleteMessage,
    setStreamingContent,
    setIsStreaming,
    aiSettings,
    sidebarLeftCollapsed,
    setCurrentConversation,
  } = useChatStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    // 监听粘贴事件
    const handlePaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData('text');
      if (text && textAreaRef.current) {
        const currentValue = textAreaRef.current.resizableTextArea?.textArea?.value || inputValue;
        const newValue = currentValue + text;
        if (newValue.length <= MAX_LENGTH) {
          setInputValue(newValue);
        } else {
          message.warning(`内容不能超过${MAX_LENGTH}个字符`);
        }
      }
    };

    // 监听快捷提问事件
    const handleSetInputValue = (event: CustomEvent) => {
      const { value } = event.detail;
      if (value && value.length <= MAX_LENGTH) {
        setInputValue(value);
        // 自动聚焦到输入框
        setTimeout(() => {
          textAreaRef.current?.focus();
        }, 100);
      }
    };

    const textArea = textAreaRef.current?.resizableTextArea?.textArea;
    if (textArea) {
      textArea.addEventListener('paste', handlePaste);
    }
    window.addEventListener('setInputValue', handleSetInputValue as EventListener);

    // 监听聚焦输入框事件
    const handleFocusInput = () => {
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 100);
    };
    window.addEventListener('focusInput', handleFocusInput as EventListener);

    return () => {
      if (textArea) {
        textArea.removeEventListener('paste', handlePaste);
      }
      window.removeEventListener('setInputValue', handleSetInputValue as EventListener);
      window.removeEventListener('focusInput', handleFocusInput as EventListener);
    };
  }, [inputValue]);

  const getGuestUserTurnCount = (): number => {
    if (!currentConversationId) return 0;
    const conversation = conversations.find((conv) => conv.id === currentConversationId);
    if (!conversation) return 0;
    return conversation.messages.filter((msg) => msg.role === 'user').length;
  };

  /** 未登录且当前对话已用完免费轮次（已发满 3 条用户消息） */
  const guestRoundLimitReached = () =>
    !isAuthenticated && getGuestUserTurnCount() >= MAX_GUEST_ROUNDS_PER_CONVERSATION;

  /** 未登录且仍有免费轮次时的提示文案（按已发送的用户消息条数） */
  const getGuestRoundReminder = (): string | null => {
    if (isAuthenticated || guestRoundLimitReached()) return null;
    const turns = getGuestUserTurnCount();
    if (turns === 0) {
      return `未登录用户：同一对话中可免费追问最多 ${MAX_GUEST_ROUNDS_PER_CONVERSATION} 轮，登录后可不限次数。`;
    }
    if (turns === 1) {
      return '您已完成第 1 轮，本对话还可免费追问 2 轮。';
    }
    if (turns === 2) {
      return '您已完成第 2 轮，还剩最后 1 轮免费追问；下一轮结束后需登录方可继续。';
    }
    return null;
  };

  const handleSend = async () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) {
      message.warning('请输入消息内容');
      return;
    }

    if (trimmedValue.length > MAX_LENGTH) {
      message.warning(`消息内容不能超过${MAX_LENGTH}个字符`);
      return;
    }

    if (guestRoundLimitReached()) {
      setLoginReason('limit');
      setLoginModalVisible(true);
      return;
    }

    speechService.stop();
    await executeSendMessage(trimmedValue);
  };

  // 提取发送消息的逻辑为独立函数
  const executeSendMessage = async (trimmedValue: string) => {

    // 停止录音（如果正在运行）
    if (isRecording) {
      stopRecording();
    }

    // 确保有当前对话
    let conversationId = currentConversationId;
    
    try {
      if (!conversationId) {
        // 没有当前对话，直接创建一个真实的（带第一条消息）
        conversationId = await createConversation(trimmedValue);
      } else if (conversationId.startsWith('conv_')) {
        // 当前是纯本地临时对话，需要升级为服务器对话
        conversationId = await promoteTemporaryConversation(conversationId, trimmedValue);
      }
    } catch (error) {
      console.error('Failed to ensure conversation:', error);
      message.error('创建对话失败，请重试');
      return;
    }

    // 添加用户消息
    const userMessageId = `msg_${Date.now()}_user`;
    const userMessage = {
      id: userMessageId,
      role: 'user' as const,
      content: trimmedValue,
      timestamp: Date.now(),
    };
    addMessage(conversationId, userMessage);

    // 清空输入框
    setInputValue('');
    setIsSending(true);
    setIsStreaming(true);
    setStreamingContent('');

    // 创建AI消息占位符（立即创建，用于显示流式内容）
    const aiMessageId = `msg_${Date.now()}_assistant`;
    let accumulatedContent = '';
    let matchedMedia: { images: string | null; videos: string | null } | null = null;

    // 立即创建空的AI消息占位符，这样流式内容可以实时显示
    const aiMessagePlaceholder: Message = {
      id: aiMessageId,
      role: 'assistant',
      content: '', // 初始为空，流式内容会通过streamingContent显示
      timestamp: Date.now(),
    };
    addMessage(conversationId, aiMessagePlaceholder);

    try {
      await sendChatMessageStream(
        conversationId, // 现在conversationId应该是真实的ID（字符串格式的数字）
        trimmedValue,
        (content) => {
          accumulatedContent += content;
          setStreamingContent(accumulatedContent);
        },
        (media) => {
          matchedMedia = media;
        },
        (error) => {
          const needLogin = Boolean((error as Error & { requiresLogin?: boolean }).requiresLogin);
          if (needLogin) {
            setLoginModalVisible(true);
            message.warning(error.message || '请登录后继续对话');
          } else {
            message.error(`发送失败: ${error.message}`);
          }
          setIsSending(false);
          setIsStreaming(false);
          setStreamingContent('');
          if (conversationId) {
            deleteMessage(conversationId, aiMessageId);
            deleteMessage(conversationId, userMessageId);
          }
        },
        () => {
          // 流式响应完成，更新AI消息内容
          if (conversationId && accumulatedContent) {
            updateMessage(
              conversationId, 
              aiMessageId, 
              accumulatedContent, 
              matchedMedia?.images, 
              matchedMedia?.videos
            );
          } else if (conversationId) {
            // 如果没有内容，删除占位符消息
            deleteMessage(conversationId, aiMessageId);
          }
          setIsSending(false);
          setIsStreaming(false);
          setStreamingContent('');
          onSend?.();
        },
        aiSettings.temperature,
        aiSettings.maxLength
      );
    } catch (error) {
      message.error('发送消息失败');
      setIsSending(false);
      setIsStreaming(false);
      setStreamingContent('');
    }
  };

  const handleClear = () => {
    setInputValue('');
    if (isRecording) {
      stopRecording();
    }
    message.success('已清空输入');
  };

  const startRecording = async () => {
    try {
      // 检查浏览器是否支持录音
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        message.error('当前浏览器不支持录音功能，请更换浏览器');
        return;
      }

      // 针对安卓/iOS优化的音频约束
      const constraints = {
        audio: {
          sampleRate: 44100,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // 检测支持的 MIME 类型
      const types = ['audio/webm', 'audio/ogg', 'audio/mp4', 'audio/wav', 'audio/mpeg'];
      const supportedType = types.find(type => MediaRecorder.isTypeSupported(type)) || '';
      
      const mediaRecorder = new MediaRecorder(stream, supportedType ? { mimeType: supportedType } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // 如果用户选择了取消，则不进行转录
        if (isCancelAreaRef.current) {
          audioChunksRef.current = [];
        } else {
          const mimeType = mediaRecorder.mimeType || supportedType || 'audio/webm';
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          if (audioBlob.size > 0) {
            await handleTranscribe(audioBlob, mimeType);
          }
        }
        // 停止所有轨道以释放麦克风
        stream.getTracks().forEach(track => track.stop());
        // 重置状态
        isCancelAreaRef.current = false;
        setIsCancelArea(false);
      };

      // 每 1000ms 触发一次 ondataavailable
      mediaRecorder.start(1000);
      setIsRecording(true);
      setShowVoiceOverlay(true);
      isCancelAreaRef.current = false;
      setIsCancelArea(false);
      // message.info('正在录制声音，请说话...');
    } catch (err: any) {
      console.error('Failed to start recording:', err);
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        message.error('未找到麦克风设备，请确保已连接麦克风');
      } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        message.error('麦克风权限被拒绝，请在浏览器设置中开启');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        message.error('麦克风可能被其他程序占用，或当前环境不支持录音');
      } else {
        message.error('无法开启麦克风，请检查权限设置或设备连接');
      }
      setIsRecording(false);
      setShowVoiceOverlay(false);
    }
  };

  const stopRecording = (cancel: boolean = false) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      // 如果明确要求取消，先更新状态
      if (cancel) {
        isCancelAreaRef.current = true;
        setIsCancelArea(true);
      }
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setShowVoiceOverlay(false);
    }
  };

  const handleTranscribe = async (audioBlob: Blob, mimeType: string) => {
    setIsTranscribing(true);
    try {
      const result = await transcribeAudio(audioBlob, mimeType);
      // 后端返回格式被 request.ts 拦截器统一包装成了 { code: 200, data: { text: '...' } }
      const text = result.data?.text || result.text;
      if (text) {
        setInputValue(prev => {
          const newValue = prev ? `${prev} ${text}` : text;
          return newValue.length <= MAX_LENGTH ? newValue : prev;
        });
      } else {
        message.info('未能识别到文字，请重试');
      }
    } catch (err: any) {
      console.error('Transcription error:', err);
      const errMsg = err.response?.data?.error || err.message || '语音识别失败';
      message.error(`${errMsg}，请重试`);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleVoiceInputStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setLoginReason('voice');
      setLoginModalVisible(true);
      return;
    }
    if (isSending || isTranscribing) return;
    
    isCancelAreaRef.current = false;
    setIsCancelArea(false);
    startRecording();
  };

  const handleVoiceInputEnd = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (isRecording) {
      stopRecording(isCancelAreaRef.current);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isRecording) return;
    
    // 阻止页面滚动
    if (e.cancelable) {
      e.preventDefault();
    }
    
    const touch = e.touches[0];
    const buttonRect = e.currentTarget.getBoundingClientRect();
    if (touch.clientY < buttonRect.top - 50) {
      isCancelAreaRef.current = true;
      setIsCancelArea(true);
    } else {
      isCancelAreaRef.current = false;
      setIsCancelArea(false);
    }
  };

  const handleVoiceInput = () => {
    // 保留此函数以防其他地方调用，但主要交互已改为按住
    if (!isAuthenticated) {
      setLoginReason('voice');
      setLoginModalVisible(true);
      return;
    }

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSending) {
        handleSend();
      }
    }
  };

  const handleGoToLogin = () => {
    setLoginModalVisible(false);
    if (onRequestLogin) {
      onRequestLogin();
    } else {
      navigate('/login');
    }
  };

  const guestReminderText = getGuestRoundReminder();

  return (
    <>
      <div 
        ref={inputAreaRef}
        className="input-area"
      >
        {guestRoundLimitReached() && (
          <div
            style={{
              padding: '8px 12px',
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '4px',
              marginBottom: '8px',
              fontSize: '12px',
              color: '#d46b08',
              textAlign: 'center',
            }}
          >
            未登录用户在同一对话中最多可追问 {MAX_GUEST_ROUNDS_PER_CONVERSATION}{' '}
            轮，已达上限。请登录后继续对话，或点击左侧「向AI提问」新建对话再试。
          </div>
        )}
        {guestReminderText && (
          <div
            style={{
              padding: '8px 12px',
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: '4px',
              marginBottom: '8px',
              fontSize: '12px',
              color: '#0958d9',
              textAlign: 'center',
            }}
          >
            {guestReminderText}
          </div>
        )}
        <div className="input-wrapper">
          <Button
            className="input-type-toggle"
            icon={inputType === 'text' ? <AudioOutlined /> : <SendOutlined rotate={-90} />}
            onClick={() => setInputType(inputType === 'text' ? 'voice' : 'text')}
          />
          
          <TextArea
            ref={textAreaRef}
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= MAX_LENGTH) {
                setInputValue(value);
              } else {
                message.warning(`内容不能超过${MAX_LENGTH}个字符`);
              }
            }}
            placeholder={inputType === 'text' ? "输入您的问题...（Shift+Enter换行，Enter发送）" : "语音转文字内容将显示在这里..."}
            autoSize={{ minRows: 1, maxRows: 4 }}
            onKeyDown={handleKeyDown}
            disabled={isSending || guestRoundLimitReached()}
            className={`chat-input ${inputType === 'voice' ? 'voice-mode-input' : ''}`}
            maxLength={MAX_LENGTH}
          />
          
          <div className="input-right-actions">
            {inputValue.trim() ? (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                loading={isSending}
                disabled={!inputValue.trim() || isSending || guestRoundLimitReached()}
                className="send-button-circle"
              />
            ) : (
              <Button
                icon={<ClearOutlined />}
                onClick={handleClear}
                disabled={!inputValue || isSending}
                title="清空"
                className="clear-button"
              />
            )}
          </div>
        </div>

        {inputType === 'voice' && (
          <div className="voice-input-container">
            <div 
              className={`voice-input-button ${isRecording ? 'recording' : ''}`}
              onMouseDown={handleVoiceInputStart}
              onMouseUp={handleVoiceInputEnd}
              onMouseLeave={handleVoiceInputEnd}
              onTouchStart={handleVoiceInputStart}
              onTouchEnd={handleVoiceInputEnd}
              onTouchMove={handleTouchMove}
            >
              {isTranscribing ? (
                <span><LoadingOutlined /> 正在识别...</span>
              ) : isRecording ? (
                <span>松开 发送</span>
              ) : (
                <span>按住 说话</span>
              )}
            </div>
          </div>
        )}
      </div>

      {showVoiceOverlay && (
        <div className={`voice-overlay ${isCancelArea ? 'cancel' : ''}`}>
          <div className="voice-overlay-content">
            <div className="voice-wave">
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
              <div className="wave-bar"></div>
            </div>
            <div className="voice-icon">
              {isCancelArea ? <ClearOutlined /> : <AudioOutlined />}
            </div>
            <div className="voice-tip">
              {isCancelArea ? '松开手指，取消发送' : '手指上滑，取消发送'}
            </div>
          </div>
        </div>
      )}

      <Modal
        title="需要登录"
        open={loginModalVisible}
        onOk={handleGoToLogin}
        onCancel={() => setLoginModalVisible(false)}
        okText="去登录"
        cancelText="取消"
        okButtonProps={{ icon: <LoginOutlined /> }}
      >
        {loginReason === 'voice' ? (
          <>
            <p>语音输入功能仅对登录用户开放。</p>
            <p>登录后即可使用语音快捷提问，交流更方便。</p>
          </>
        ) : (
          <>
            <p>
              未登录用户在同一对话中最多可免费追问 {MAX_GUEST_ROUNDS_PER_CONVERSATION}{' '}
              轮，当前对话已达上限。
            </p>
            <p>登录后可继续在当前对话中无限追问，也可新建对话继续使用。</p>
          </>
        )}
      </Modal>
    </>
  );
};

export default InputArea;
